/**
 * HelpNav — horizontal section tab bar at the top of the help drawer.
 * Mirrors XTS HelpNav (which uses Tailwind + FontAwesome) but uses
 * PlayStudio's IconName-based Icon component and inline styles tied
 * to the design system CSS vars.
 */

import { Icon } from "../icons";
import type { HelpSection } from "./helpData";

interface HelpNavProps {
  sections: HelpSection[];
  activeSection: string;
  onSectionChange: (sectionId: string) => void;
}

export function HelpNav({ sections, activeSection, onSectionChange }: HelpNavProps) {
  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        borderBottom: "1px solid var(--border)",
        overflowX: "auto",
        flexShrink: 0,
      }}
    >
      {sections.map((section) => {
        const isActive = activeSection === section.id;
        return (
          <button
            key={section.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${section.id}`}
            onClick={() => onSectionChange(section.id)}
            data-testid={`help-tab-${section.id}`}
            style={{
              flexShrink: 0,
              padding: "10px 14px",
              fontSize: 12.5,
              fontWeight: isActive ? 600 : 500,
              color: isActive ? "var(--text)" : "var(--text-3)",
              background: isActive ? "var(--bg-soft)" : "transparent",
              border: 0,
              borderBottom: `2px solid ${isActive ? "var(--accent)" : "transparent"}`,
              marginBottom: -1,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <Icon name={section.icon} className="ic-svg sm" />
            <span dangerouslySetInnerHTML={{ __html: section.title }} />
          </button>
        );
      })}
    </div>
  );
}
